import { Component, type ErrorInfo, type ReactNode } from "react";

import { rechargerSiChunkPerdu } from "@/core/pwa/chunks";
import { Button, Carte } from "@/shared/ui";
import { AlertCircle } from "@/shared/ui/icons";

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Generic error boundary. Wraps the cockpit shell at the route level
 * and can be used to wrap individual panels for fault isolation.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Un chunk disparu n'est pas un bug du panneau : c'est une fenetre restee
    // ouverte sur le bundle d'avant un deploiement, qui demande un fichier que
    // l'image servie ne contient plus. On recharge UNE fois (cf `chunks.ts`)
    // plutot que d'afficher « Panel error » et un message que personne ne peut
    // interpreter. Depuis qu'on ne recharge plus de force un chatteur en plein
    // message, c'est ce filet qui rend le report sans danger.
    if (rechargerSiChunkPerdu(error)) return;
    console.error("[Cockpit ErrorBoundary]", error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return (
        <div className="flex h-full w-full items-center justify-center p-8">
          <Carte remplissage="lg" className="max-w-lg" role="alert">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-erreur" aria-hidden />
              <div className="min-w-0 flex-1 space-y-3">
                <h2 className="text-[16px] font-semibold text-fg">Panel error</h2>
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-[12px] text-muted">
                  {this.state.error.message}
                </pre>
                <Button variant="secondary" size="lg" onClick={this.reset}>
                  Retry
                </Button>
              </div>
            </div>
          </Carte>
        </div>
      );
    }
    return this.props.children;
  }
}
